import { Fragment, Suspense, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import {
  CircularProgress,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useMatch } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  iconsApiRef,
  useApi,
  useRouteRef,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import type { IconComponent } from '@backstage/frontend-plugin-api';
import {
  CATALOG_FILTER_EXISTS,
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { autheliaAuthApiRef } from '../../auth/autheliaAuthApi';
import {
  branchOptions,
  fetchArgoJson,
  healthColor,
  type ArgoApplication,
  type BranchOption,
} from '../../components/odooEnvironments/argoApp';
import {
  ODOO_BRANCH_PATH,
  odooBranchRouteRef,
} from '../../components/odooEnvironments/branchRouteRef';
import { odooEnvironmentsTranslationRef } from '../../components/odooEnvironments/translation';

/**
 * One row of the block: an Odoo project, and where its catalog entity lives.
 *
 * `projectId` is the value of the `odoo/project-id` annotation — the identity
 * ArgoCD knows the project by — while `label` is what a human called it. The
 * two are kept apart because the sidebar shows one and queries with the other.
 */
interface OdooProjectNavItem {
  /** React key. Two entities may carry the same annotation; refs are unique. */
  entityRef: string;
  projectId: string;
  label: string;
  params: ReturnType<typeof entityRouteParams>;
}

/** Which project was expanded last, so a reload reopens the same one. */
const STORAGE_KEY = '@waadoo/odoo-nav/expanded-project';

function readStoredProject(): string | undefined {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    // Guarded the way Backstage guards its own sidebar pin state
    // (core-components' Sidebar/localStorage.esm.js): reading localStorage
    // throws outright in a browser where storage is disabled, and losing the
    // last-opened project must not take the whole sidebar down with it.
    return undefined;
  }
}

function writeStoredProject(projectId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, projectId);
  } catch {
    // Same reason as above, plus the quota: this is a convenience.
  }
}

/**
 * The single place where ArgoCD's health vocabulary becomes a colour in this
 * sidebar. `healthColor` in argoApp.ts stays the only source of the semantics
 * — it names the four cases — and this record only says which MUI palette
 * entry each name paints with. Keyed by that union, so a fifth case there
 * fails to compile here instead of silently falling through to grey.
 */
const healthDotColors: Record<
  ReturnType<typeof healthColor>,
  (palette: Theme['palette']) => string
> = {
  success: palette => palette.success.main,
  warning: palette => palette.warning.main,
  danger: palette => palette.error.main,
  secondary: palette => palette.text.secondary,
};

const useStyles = makeStyles<Theme>(theme => ({
  sectionTitle: {
    color: theme.palette.navigation.color,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: 1,
    opacity: 0.7,
    padding: theme.spacing(1.5, 2, 0.5, 3),
    textTransform: 'uppercase',
  },
  statusRow: {
    alignItems: 'center',
    color: theme.palette.navigation.color,
    display: 'flex',
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.7,
    padding: theme.spacing(0.5, 2, 0.5, 6),
  },
  statusSpinner: {
    marginRight: theme.spacing(1),
  },
  // Only a left padding: SidebarItem's own `root` class sets none, so nothing
  // here depends on which stylesheet JSS injected first.
  branchItem: {
    paddingLeft: theme.spacing(3),
  },
  healthDot: {
    display: 'inline-flex',
  },
}));

function OdooProjectsNavContent() {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const { isOpen } = useSidebarOpenState();
  const { t } = useTranslationRef(odooEnvironmentsTranslationRef);

  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const autheliaAuth = useApi(autheliaAuthApiRef);
  const iconsApi = useApi(iconsApiRef);

  const entityRoute = useRouteRef(entityRouteRef);
  const branchRoute = useRouteRef(odooBranchRouteRef);
  // A boolean and not `branchRoute` itself in the effect below: useRouteRef
  // memoises on the current pathname, so the function's identity changes on
  // every in-app navigation, and an effect depending on it would re-run — and
  // re-query ArgoCD — each time the user moves. Whether the route exists at
  // all does not change while the app is running.
  const canLinkBranches = Boolean(branchRoute);

  // The URL is the second source of truth for "which project": on
  // /odoo/<project>/<branch> the sidebar opens that project even though
  // nothing was clicked. Matched against the very literal the page extension
  // mounts on, so the two cannot drift apart.
  const branchMatch = useMatch(ODOO_BRANCH_PATH);

  const [projects, setProjects] = useState<OdooProjectNavItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [clickedProject, setClickedProject] = useState<string>();
  // Read once, at mount: afterwards the click and the URL decide, and re-reading
  // storage could only fight them.
  const [lastVisitedProject] = useState(readStoredProject);
  const [branchesByProject, setBranchesByProject] = useState<
    Record<string, BranchOption[]>
  >({});
  const [loadingProject, setLoadingProject] = useState<string>();

  // Exactly one project open, and this expression is the whole rule: the last
  // click wins, then the branch page one is reading, then the last visit.
  const expandedProject =
    clickedProject ?? branchMatch?.params.project ?? lastVisitedProject;

  useEffect(() => {
    let active = true;

    const loadProjects = async () => {
      try {
        // The same predicate the environments card's extension declares in
        // components/odooEnvironments/plugin.tsx — an Odoo project is an entity
        // carrying `odoo/project-id`, and there must be one definition of that,
        // not two.
        const { items } = await catalogApi.getEntities({
          filter: {
            'metadata.annotations.odoo/project-id': CATALOG_FILTER_EXISTS,
          },
          fields: [
            'kind',
            'metadata.namespace',
            'metadata.name',
            'metadata.title',
            'metadata.annotations',
          ],
        });

        const found = items
          .flatMap(entity => {
            // The filter guarantees the annotation is there; the type does not,
            // and reading the value is the only way to get the project id.
            const projectId = entity.metadata.annotations?.['odoo/project-id'];
            return projectId
              ? [
                  {
                    entityRef: stringifyEntityRef(entity),
                    projectId,
                    label: entity.metadata.title ?? entity.metadata.name,
                    params: entityRouteParams(entity),
                  },
                ]
              : [];
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        if (active) {
          setProjects(found);
        }
      } catch {
        // Swallowed on purpose. Before sign-in every backend call fails, and a
        // navigation bar is not the place to report it: the block simply does
        // not appear. Errors from the catalog and from ArgoCD keep being shown
        // where they can be acted upon — on the entity card and on the branch
        // page.
        if (active) {
          setProjects([]);
        }
      } finally {
        if (active) {
          setProjectsLoading(false);
        }
      }
    };

    void loadProjects();

    return () => {
      active = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    // Branches are read on expand and never before: one project's Applications
    // per click is what keeps this block from costing a request per project on
    // every page load.
    if (!expandedProject || !canLinkBranches) {
      return undefined;
    }

    // Memoised per project id: collapsing and re-expanding costs nothing. A
    // *failed* read is deliberately not memoised, so re-expanding retries it.
    if (branchesByProject[expandedProject]) {
      return undefined;
    }

    let active = true;

    const loadBranches = async (projectId: string) => {
      setLoadingProject(projectId);

      try {
        const list = await fetchArgoJson<{ items?: ArgoApplication[] | null }>({
          fetchApi,
          discoveryApi,
          autheliaAuth,
          noIdentityMessage: t('error.noIdentity'),
          notJsonMessage: (body: string) => t('error.notJson', { body }),
          path: `applications?selector=${encodeURIComponent(
            `odoo-project=${projectId}`,
          )}`,
        });

        if (!active) {
          return;
        }
        // ArgoCD returns items: null, not [], when nothing matches.
        setBranchesByProject(current => ({
          ...current,
          [projectId]: branchOptions(list.items ?? []),
        }));
      } catch {
        // Swallowed for the reason given on the catalog read above: nothing is
        // rendered under the project, and the card and the branch page remain
        // where an ArgoCD failure is reported.
      } finally {
        if (active) {
          setLoadingProject(undefined);
        }
      }
    };

    void loadBranches(expandedProject);

    return () => {
      active = false;
    };
  }, [
    expandedProject,
    branchesByProject,
    canLinkBranches,
    fetchApi,
    discoveryApi,
    autheliaAuth,
    t,
  ]);

  useEffect(() => {
    if (expandedProject) {
      writeStoredProject(expandedProject);
    }
  }, [expandedProject]);

  const StatusDotIcon = iconsApi.getIcon('status-dot');

  const healthDotIcon = (health: string): IconComponent =>
    function HealthDot(props) {
      return (
        <span
          className={classes.healthDot}
          style={{ color: healthDotColors[healthColor(health)](theme.palette) }}
        >
          {/* The icon is registered in modules/icons.tsx, so the guard is the
              compiler's and not a case that can occur: getIcon is typed as
              possibly returning undefined for a name nobody registered. */}
          {StatusDotIcon ? <StatusDotIcon {...props} /> : null}
        </span>
      );
    };

  const renderBranches = (projectId: string): ReactNode => {
    // Same guard as BranchSwitcher: undefined when the `odoo` page plugin is
    // not registered. A branch row that cannot link anywhere is not worth a
    // line, so the project keeps its own row and gains nothing under it.
    if (!branchRoute) {
      return null;
    }

    if (loadingProject === projectId) {
      return (
        <div className={classes.statusRow}>
          <CircularProgress
            size={12}
            color="inherit"
            className={classes.statusSpinner}
          />
          {t('nav.loadingBranches')}
        </div>
      );
    }

    const branches = branchesByProject[projectId];

    if (!branches) {
      return null;
    }

    if (branches.length === 0) {
      return <div className={classes.statusRow}>{t('nav.noBranches')}</div>;
    }

    return branches.map(option => (
      <SidebarItem
        key={option.branch}
        className={classes.branchItem}
        icon={healthDotIcon(option.health)}
        text={option.branch}
        to={branchRoute({ project: projectId, branch: option.branch })}
      />
    ));
  };

  // A closed sidebar shows icons and no text. This block is a list of names —
  // without them it would be the same chevron repeated once per project — so it
  // waits until the sidebar is open. The branch switcher on the page itself is
  // what remains reachable meanwhile.
  if (!isOpen) {
    return null;
  }

  // `entityRoute` is undefined when the catalog plugin is not registered, which
  // is also the plugin every project row points into: without it the block has
  // nowhere to lead.
  if (!entityRoute) {
    return null;
  }

  // Nothing to show and nothing to say: no title, no empty area. This is also
  // what a signed-out visitor gets, the catalog read above having failed.
  if (!projectsLoading && projects.length === 0) {
    return null;
  }

  return (
    <>
      <Typography component="div" className={classes.sectionTitle}>
        {t('nav.projects')}
      </Typography>
      {projectsLoading ? (
        <div className={classes.statusRow}>
          <CircularProgress
            size={12}
            color="inherit"
            className={classes.statusSpinner}
          />
        </div>
      ) : (
        projects.map(project => (
          <Fragment key={project.entityRef}>
            {/* `to` and `onClick` together: SidebarItem's link variant runs the
                handler and follows the link, which is exactly "open this
                project and go to it". */}
            <SidebarItem
              icon={
                project.projectId === expandedProject
                  ? ExpandMoreIcon
                  : ChevronRightIcon
              }
              text={project.label}
              to={entityRoute(project.params)}
              onClick={() => setClickedProject(project.projectId)}
            />
            {project.projectId === expandedProject
              ? renderBranches(project.projectId)
              : null}
          </Fragment>
        ))
      )}
    </>
  );
}

/**
 * The Odoo projects of the catalog in the main sidebar, the current one opened
 * on its branches.
 *
 * Wrapped in its own Suspense boundary: `useTranslationRef` throws a promise
 * until the ref's resource has loaded, and the nearest boundary above the
 * sidebar is the one `createApp` puts at the root — so without this the whole
 * app would blank to its loading indicator for a tick on every cold start.
 */
export function OdooProjectsNav() {
  return (
    <Suspense fallback={null}>
      <OdooProjectsNavContent />
    </Suspense>
  );
}
